import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { AsthmaSeverityEnum } from '../../../../lib/types/enums/affected-staff-questionnaire/asthma-severity.enum'
import { InterestEnum } from '../../../../lib/types/enums/affected-staff-questionnaire/intrest.enum'
import { PersonalityEnum } from '../../../../lib/types/enums/affected-staff-questionnaire/personality.enum'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../../lib/utils/validations/file.validation'
import {
  createStringArraySchema,
  createStringSchema
} from '../../../../lib/utils/validations/string-validation'

export const saveForLaterAffectedStaffQuestionnaireSchema = z.object({
  isSubmitted: z.boolean().optional(),
  personalityTraits: createEnumArraySchema({
    enumType: PersonalityEnum,
    fieldName: 'personalityTraits'
  }).nullish(),
  hasReligiousAffiliation: z.boolean().nullish(),
  otherReligiousAffiliation: createStringSchema('Laquelle', { nullish: true }),
  interests: createEnumArraySchema({
    enumType: InterestEnum,
    fieldName: 'interests'
  }).nullish(),
  willingToStayWithDifferentEthnicBackground: z.boolean().nullish(),
  isInGoodHealth: z.boolean().nullish(),
  hasAsthma: z.boolean().nullish(),
  asthmaSeverity: createEnumSchema({
    enumType: AsthmaSeverityEnum,
    fieldName: 'asthmaSeverity',
    nullish: true
  }),
  hasAllergies: z.boolean().nullish(),
  allergyType: createStringSchema('allergyType', { nullish: true }),
  isSmoker: z.boolean().nullish(),
  agreesToFamilyLifeStyle: z.boolean().nullish(),
  otherInformationInEnglish: createStringSchema('otherInformationInEnglish', { nullish: true }),
  hasDriversLicense: z.boolean().nullish(),
  drivesRegularly: z.boolean().nullish(),
  hostFamilyLetter: createStringSchema('hostFamilyLetter', { nullish: true }),
  hasMedicalAuthorization: z.boolean().nullish(),
  hobbies: createStringArraySchema('hobbies').nullish(),
  familyImages: z.array(FILE_VALIDATION()).nullish()
})

export const submitAffectedStaffQuestionnaireSchema = (
  hasHostFamily: boolean,
  t: TFunction<AvailableLanguages, undefined>
) => {
  return z
    .object({
      isSubmitted: z.boolean().optional(),
      personalityTraits: createEnumArraySchema({
        enumType: PersonalityEnum,
        fieldName: 'personalityTraits'
      }).nullish(),
      hasReligiousAffiliation: z.boolean(),
      otherReligiousAffiliation: createStringSchema('Laquelle', { nullish: true }),
      interests: createEnumArraySchema({
        enumType: InterestEnum,
        fieldName: 'interests'
      }).nullish(),
      willingToStayWithDifferentEthnicBackground: z.boolean(),
      isInGoodHealth: z.boolean(),
      hasAsthma: z.boolean(),
      asthmaSeverity: createEnumSchema({
        enumType: AsthmaSeverityEnum,
        fieldName: 'asthmaSeverity',
        nullish: true
      }),
      hasAllergies: z.boolean(),
      allergyType: createStringSchema('allergyType', { nullish: true }),
      isSmoker: z.boolean(),
      agreesToFamilyLifeStyle: z.boolean().nullish(),
      otherInformationInEnglish: createStringSchema('otherInformationInEnglish', {
        nullish: true
      }),
      hasDriversLicense: z.boolean(),
      drivesRegularly: z.boolean().nullish(),
      hostFamilyLetter: createStringSchema('hostFamilyLetter'),
      hasMedicalAuthorization: z.boolean(),
      hobbies: createStringArraySchema('hobbies').nullish(),
      familyImages: z.array(FILE_VALIDATION()).nullish()
    })
    .superRefine((data, ctx) => {
      if (hasHostFamily) {
        if (!data.hostFamilyLetter) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hostFamilyLetter'],
            message: t('errors.string.required', {
              fieldName: t('affected.staff.questionnaire.host.family.letter')
            })
          })
        }
        if (!data.familyImages || data.familyImages.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['familyImages'],
            message: t('questionnaire.toast.error.familyImages')
          })
        }
      }
      if (data.hasReligiousAffiliation && !data.otherReligiousAffiliation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('errors.string.required', {
            fieldName: t('affected.staff.questionnaire.which.one')
          }),
          path: ['otherReligiousAffiliation']
        })
      }
      if (data.hasAsthma && !data.asthmaSeverity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('errors.string.required'),
          path: ['asthmaSeverity']
        })
      }
      if (data.hasAllergies && !data.allergyType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('errors.string.required', {
            fieldName: t('affected.staff.questionnaire.which.one')
          }),
          path: ['allergyType']
        })
      }

      if (data.isSmoker && data.agreesToFamilyLifeStyle === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('errors.string.required'),
          path: ['agreesToFamilyLifeStyle']
        })
      }

      if (data.hasDriversLicense && data.drivesRegularly === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('errors.string.required', {
            fieldName: t('affected.staff.questionnaire.drives.regularly')
          }),
          path: ['drivesRegularly']
        })
      }
    })
}
